import { date } from 'quasar'
import Activity from 'src/models/activity/Activity'
import ActivityBooking from 'src/models/activity/ActivityBooking'
import ActivitySchedule from 'src/models/activity-schedule/ActivitySchedule'
import ActivitySpace from 'src/models/activity-space/ActivitySpace'
import Generic from 'src/models/generic/Generic'
import Model, { encoders, ID, IsoDateTime, MODEL_LEVEL } from 'src/models/model'
import { validateDayNumber } from 'src/models/schedule/BookingWeek'
import TimeSlot, { START_HOUR } from 'src/models/schedule/TimeSlot'
import PriceCard from 'src/models/showing/PriceCard'

const { addToDate } = date

export default class ActivitySession extends Model {
  static name = 'ActivitySession'

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.NonFilmProgramming]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      activity: {
        type: Activity,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      activitySpace: {
        type: ActivitySpace,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      activitySchedule: {
        type: ActivitySchedule,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      activityBookings: {
        type: [ActivityBooking],
        output: true,
        default: () => ([]),
        excludeFromDefault: true
      },
      priceCard: {
        type: PriceCard,
        reference: true,
        output: true
      },
      overridePriceCard: {
        type: Boolean,
        default: false
      },
      customPriceCard: {
        type: PriceCard,
        reference: true,
        default: null,
        encode: encoders.hasOne
      },
      startTime: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      endTime: {
        type: IsoDateTime,
        default: null,
        sortable: true
      },
      published: {
        type: Boolean,
        default: false
      },
      hasTicketsSold: {
        type: Boolean,
        output: true
      },
      availableEntries: {
        type: [IsoDateTime],
        excludeFromDefault: true,
        output: true
      }
    }

    this.computed = {
      siteStartTime () {
        if (!this.startTime) return this.startTime

        return this.startTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      siteEndTime () {
        if (!this.endTime) return this.endTime

        return this.endTime.inTimeZone(this.configuration.currentSite?.timeZone)
      },
      timeSlot () {
        if (!this.siteStartTime) return

        const timeSlot = TimeSlot.timeSlotForDateTime(this.siteStartTime)
        if (!timeSlot) {
          throw new Error(`No matching time slot for ${this.siteStartTime} ${this.id}`)
        }
        return timeSlot
      },
      dayNumber () {
        if (!this.startTime) return

        return new IsoDateTime(addToDate(this.startTime, { hours: -START_HOUR })).inTimeZone(this.configuration.currentSite?.timeZone).getDay()
      }
    }

    this.queries = {
      activitySessionsAndAvailableEntriesForActivityBooking: {
        arguments: {
          activityBookingId: {
            type: ID,
            required: true
          },
          shouldRestrictToSamePriceCard: {
            type: Boolean,
            required: false
          },
          date: {
            type: String,
            required: false
          }
        },
        results: {
          value: Generic
        }
      }
    }

    this.paginate = true
  }

  setActivityScheduleDate (startOfWeek) {
    while (this.startTime > startOfWeek) {
      this.startTime = date.addToDate(this.startTime, { days: -7 })
    }
    while (this.startTime < startOfWeek) {
      this.startTime = date.addToDate(this.startTime, { days: 7 })
    }
  }

  setDayNumber (dayNumber, activityScheduleStartDayNumber) {
    const daysFromActivityScheduleStart = validateDayNumber(dayNumber - activityScheduleStartDayNumber)
    const currentTime = new IsoDateTime(this.startTime).inTimeZone(this.configuration.currentSite.timeZone)
    const currentDaysFromActivityScheduleStart = validateDayNumber(currentTime.getDay() - activityScheduleStartDayNumber)
    this.startTime = date.addToDate(this.startTime, { days: daysFromActivityScheduleStart - currentDaysFromActivityScheduleStart })
  }

  addMinutes (minutes) {
    this.startTime = new IsoDateTime(addToDate(this.startTime, { minutes }))
    this.endTime = new IsoDateTime(addToDate(this.endTime, { minutes }))
  }

  get hasStarted () {
    const currentTime = new IsoDateTime()
    return this.startTime < currentTime
  }

  get hasEnded () {
    const currentTime = new IsoDateTime()
    return this.endTime < currentTime
  }

  isStartingSoon (withinMinutes = 30) {
    return !this.hasStarted && this.startTime < addToDate(new Date(), { minutes: withinMinutes })
  }
}
